import React, { useState } from "react";
import { reduceMotion, shade } from "./fx";
import { BALLS } from "./ballThemes";

/**
 * Pure CSS-3D market objects for the collectible card cockpit.
 *
 * The handoff ships one object per category; the cockpit is scoped to the two
 * Pokémon card markets, so only the two collectible shapes are ported:
 *   - `slab` — PSA-style sealed acrylic grading case  → PSA-10 index
 *   - `pack` — foil booster pack with holo sheen      → POKE index
 * No image assets: everything below is HTML + CSS.
 */

/** Absolutely-positioned 3D face helper (handoff: `face`). */
const face = (w, h, tf, bg, extra = {}) => ({
  position: "absolute",
  left: "50%",
  top: "50%",
  width: w,
  height: h,
  marginLeft: -w / 2,
  marginTop: -h / 2,
  transform: tf,
  background: bg,
  backfaceVisibility: "hidden",
  ...extra,
});

/**
 * The markings that distinguish one ball from another — everything above the
 * band and below the button. Poké has none; that is what makes it the Poké.
 * @param {object} b - ball descriptor
 * @param {Function} u - fraction-of-diameter helper
 */
function ballMarkings(b, u) {
  if (b.key === "great") {
    // One stripe per side, mirrored, raked back off the dome's curve.
    return [-28, 28].map((deg) => (
      <div
        key={deg}
        style={{
          position: "absolute",
          top: u(0.14),
          [deg < 0 ? "left" : "right"]: u(0.05),
          width: u(0.34),
          height: u(0.13),
          borderRadius: u(0.03),
          background: "linear-gradient(180deg,#D0505A,#9E2733)",
          transform: `rotate(${deg}deg)`,
          boxShadow: "0 1px 0 rgba(11,18,32,.3)",
        }}
      />
    ));
  }
  if (b.key === "ultra") {
    return (
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: u(0.13),
          width: u(0.4),
          height: u(0.42),
          marginLeft: -u(0.2),
          borderRadius: `${u(0.05)}px ${u(0.05)}px 0 0`,
          background: "linear-gradient(180deg,#35363A,#17181B)",
        }}
      />
    );
  }
  if (b.key === "master") {
    return (
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: u(0.1),
          textAlign: "center",
          fontSize: u(0.34),
          fontWeight: 700,
          lineHeight: 1,
          color: "#fff",
          textShadow: "0 1px 2px rgba(11,18,32,.45)",
        }}
      >
        M
      </div>
    );
  }
  return null;
}

/**
 * CSS ball emblem, in any of the four themes. Drawn rather than using
 * /assets/images/Perps/pokeball.svg because that file ships without its fill
 * classes and renders as a black disc.
 *
 * One implementation covers all three sizes it is asked for — the 44px loader
 * button, the 26px chart end-marker and the 22px slider knobs — so every layer
 * is a fraction of the diameter rather than a fixed px value.
 *
 * @param {{d?: number, ball?: object|string, style?: object}} props - `d` is
 *   the diameter in px; `ball` is a descriptor or key, defaulting to Poké
 */
export function PokeBall({ d = 60, ball, style = {} }) {
  const b =
    (typeof ball === "string" ? BALLS.find((x) => x.key === ball) : ball) || BALLS[0];
  const u = (n) => d * n;

  return (
    <div
      style={{
        width: d,
        height: d,
        borderRadius: "50%",
        position: "relative",
        overflow: "hidden",
        background: "radial-gradient(circle at 34% 74%, #fff 0%, #f0f1f4 46%, #c9ccd4 100%)",
        boxShadow:
          "inset 0 3px 6px rgba(255,255,255,0.5), inset 0 -4px 8px rgba(0,0,0,0.35), 0 3px 8px rgba(0,0,0,0.4)",
        ...style,
      }}
    >
      {/* Dome — the top half, and the only part that carries the ball's hue. */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "50%",
          background: `radial-gradient(115% 150% at 32% 12%, ${shade(b.dome, 0.55)} 0%, ${b.dome} 42%, ${b.domeLo} 100%)`,
        }}
      />

      {/* Master's two pale lobes sit under its M, not over the dome edge. */}
      {b.key === "master" &&
        [-1, 1].map((s) => (
          <div
            key={s}
            style={{
              position: "absolute",
              top: u(0.06),
              left: "50%",
              width: u(0.52),
              height: u(0.52),
              marginLeft: s * u(0.14) - u(0.26),
              borderRadius: "50%",
              background: "radial-gradient(circle at 40% 35%, #D79BE0, #9A5FC4)",
            }}
          />
        ))}

      {ballMarkings(b, u)}

      <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: u(0.13), marginTop: -u(0.065), background: "#151520", boxShadow: "0 1px 0 rgba(255,255,255,.25)" }} />

      {/* Centre button. The ring shadows are a FRACTION of the diameter, so at
          small sizes (the chart end-marker is 30px) they used to swallow the
          dome and the lower half entirely. Clamped to keep the ball readable
          when small without changing how it looks at full size. */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: u(0.26),
          height: u(0.26),
          marginLeft: -u(0.13),
          marginTop: -u(0.13),
          borderRadius: "50%",
          background: "radial-gradient(circle at 38% 32%, #fff, #dcdee3)",
          boxShadow: `0 0 0 ${Math.min(u(0.05), 2)}px #151520, 0 0 0 ${Math.min(u(0.08), 3)}px #fff, inset 0 1px 1px rgba(11,18,32,.2)`,
        }}
      />

      {/* Gloss last, over everything, so the whole ball reads as one sphere. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(45% 32% at 30% 16%, rgba(255,255,255,.6), transparent 70%), radial-gradient(120% 90% at 50% 118%, rgba(11,18,32,.28), transparent 55%)",
        }}
      />
    </div>
  );
}
/**
 * Graded slab — a PSA-style sealed acrylic case.
 *
 * Built as real layers rather than one printed face: a thick clear shell with
 * a bevelled inner well, the grade label seated in it, and the card recessed
 * below the surface. The depth comes from paired inset shadows — light from
 * the top-left, shade at the bottom-right — on every layer, which is what
 * separates "a picture of a slab" from something that reads as moulded.
 *
 * @param {{m: object, size?: number}} props - `m` is a cockpit market descriptor
 */
export function Slab3D({ m, size = 160 }) {
  const s = size;
  const W = s * 0.68;
  const H = s * 1.06;
  const g = m.accent;
  const reduce = reduceMotion();

  const cardBg = `linear-gradient(155deg, ${shade(g, 0.5)} 0%, ${shade(g, 0.12)} 26%, ${shade(g, -0.22)} 52%, ${shade(g, 0.3)} 74%, ${shade(g, -0.4)} 100%)`;
  const labelBg = `linear-gradient(180deg, ${shade(g, 0.6)}, ${shade(g, 0.12)} 48%, ${shade(g, -0.18)})`;

  return (
    <div style={{ width: W * 1.55, height: H * 1.16, perspective: s * 5, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "relative", width: W, height: H, transformStyle: "preserve-3d", transform: "rotateY(-4deg) rotateX(2deg)" }}>
        {/* Front shell. */}
        <div
          style={face(W, H, `translateZ(0px)`,
            "linear-gradient(150deg, #ffffff 0%, #eef4f9 34%, #dce7f0 62%, #f4f9fc 100%)",
            {
              borderRadius: 9,
              overflow: "hidden",
              boxShadow: [
                "inset 0 2px 3px rgba(255,255,255,1)",       // top bevel, lit
                "inset -2px -3px 6px rgba(90,115,140,0.4)",  // bottom-right shade
                "inset 0 0 0 1px rgba(255,255,255,0.9)",
                "0 14px 30px rgba(0,0,0,0.45)",
              ].join(", "),
              padding: `${H * 0.05}px ${W * 0.085}px`,
              display: "flex",
              flexDirection: "column",
              gap: H * 0.028,
            }
          )}
        >
          {/* Grade label, seated in the shell. */}
          <div
            style={{
              height: H * 0.19,
              borderRadius: 4,
              background: labelBg,
              boxShadow: `inset 0 1px 2px rgba(255,255,255,0.75), inset 0 -2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.35)`,
              display: "flex",
              alignItems: "center",
              padding: `0 ${W * 0.06}px`,
              gap: W * 0.05,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
              <div style={{ fontSize: W * 0.135, fontWeight: 900, color: "#241a02", letterSpacing: "0.04em" }}>PSA</div>
              <div style={{ fontSize: W * 0.072, fontWeight: 800, color: "#5a4508", letterSpacing: "0.02em", marginTop: H * 0.006 }}>GEM MINT</div>
            </div>
            <div style={{ marginLeft: "auto", fontSize: W * 0.33, fontWeight: 900, color: "#1c1402", textShadow: "0 1px 0 rgba(255,255,255,0.5)", lineHeight: 0.8 }}>10</div>
            <div
              style={{
                position: "absolute", top: "-30%", bottom: "-30%", left: 0, width: "40%",
                pointerEvents: "none", mixBlendMode: "screen", willChange: "transform",
                background: "linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)",
                transform: "translateX(-160%) skewX(-14deg)",
                animation: reduce ? "none" : "ceSheenX 5s cubic-bezier(.45,0,.55,1) infinite",
              }}
            />
          </div>

          {/* The card, recessed below the shell surface. */}
          <div
            style={{
              flex: 1,
              borderRadius: 5,
              background: cardBg,
              position: "relative",
              overflow: "hidden",
              boxShadow: [
                "inset 0 2px 5px rgba(0,0,0,0.45)",           // sits below the surface
                "inset 0 -1px 0 rgba(255,255,255,0.35)",
                `inset 0 0 0 1.5px ${shade(g, 0.45)}`,
              ].join(", "),
            }}
          >
            <div style={{ position: "absolute", top: "8%", left: "50%", transform: "translateX(-50%)", filter: "drop-shadow(0 5px 10px rgba(0,0,0,0.5))" }}>
              <PokeBall d={W * 0.46} />
            </div>
            <div style={{ position: "absolute", bottom: "8%", left: 0, right: 0, textAlign: "center" }}>
              <div style={{ fontSize: W * 0.19, fontWeight: 900, color: "#fff", letterSpacing: "-0.01em", textShadow: "0 2px 6px rgba(0,0,0,0.55)", lineHeight: 1 }}>{m.short}</div>
              <div style={{ fontSize: W * 0.064, fontWeight: 700, color: "rgba(255,255,255,0.9)", letterSpacing: "0.07em", marginTop: H * 0.012 }}>
                {m.packSub || "TOP 10 GRADED CARDS"}
              </div>
            </div>
            {/* Holo sweep across the card. */}
            <div
              style={{
                position: "absolute", top: "-20%", bottom: "-20%", left: 0, width: "50%",
                pointerEvents: "none", mixBlendMode: "screen", willChange: "transform",
                background: "linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.14) 35%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0.14) 65%, transparent 100%)",
                transform: "translateX(-160%) skewX(-14deg)",
                animation: reduce ? "none" : "ceSheenX 5s cubic-bezier(.45,0,.55,1) 0.4s infinite",
              }}
            />
          </div>

          {/* Glass over the whole shell: a hard corner catch, not a wash. */}
          <div
            style={{
              position: "absolute", inset: 0, borderRadius: 9, pointerEvents: "none",
              background: [
                "linear-gradient(128deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.12) 18%, transparent 34%)",
                "linear-gradient(310deg, rgba(255,255,255,0.3) 0%, transparent 22%)",
              ].join(", "),
            }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Foil booster pack.
 *
 * The important trick is that a pack is not flat — it is a bag over cards, so
 * it bulges. A horizontal light/shade band across the face fakes that
 * cylindrical curve, and the crimped ends are drawn as real serrations rather
 * than a stripe. Two sheens at different speeds give the foil its shift.
 *
 * @param {{m: object, size?: number}} props
 */
export function Pack3D({ m, size = 160 }) {
  const s = size;
  const W = s * 0.76;
  const H = s * 1.02;
  const g = m.accent;
  const reduce = reduceMotion();
  const ring = m.ring || "#C7CBD6";

  const foil = `linear-gradient(152deg, ${shade(g, 0.55)} 0%, ${shade(g, 0.12)} 26%, ${shade(g, -0.28)} 52%, ${shade(g, 0.34)} 76%, ${shade(g, -0.45)} 100%)`;
  // Serrated crimp: alternating notches, darker in the valleys.
  const crimp = `repeating-linear-gradient(90deg, ${shade(g, -0.5)} 0 3px, ${shade(g, 0.25)} 3px 6px)`;

  return (
    <div style={{ width: W * 1.4, height: H * 1.16, perspective: s * 5, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "relative", width: W, height: H, transformStyle: "preserve-3d", transform: "rotateY(-4deg) rotateX(2deg)" }}>
        {/* Front. */}
        <div
          style={face(W, H, `translateZ(0px)`, foil, {
            borderRadius: 7,
            overflow: "hidden",
            boxShadow: [
              `inset 0 0 0 1.5px ${ring}77`,
              "inset 0 3px 7px rgba(255,255,255,0.4)",
              "inset 0 -4px 9px rgba(0,0,0,0.45)",
              "0 14px 28px rgba(0,0,0,0.45)",
            ].join(", "),
          })}
        >
          {/* Cylindrical bulge — the single biggest thing that stops it reading flat. */}
          <div
            style={{
              position: "absolute", inset: 0, pointerEvents: "none",
              background: "linear-gradient(90deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.1) 12%, rgba(255,255,255,0.22) 38%, rgba(255,255,255,0.05) 56%, rgba(0,0,0,0.18) 80%, rgba(0,0,0,0.44) 100%)",
            }}
          />

          {/* Crimped ends, with a shadow where the seal folds. */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: H * 0.075, background: crimp, boxShadow: "inset 0 -3px 5px rgba(0,0,0,0.5)" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: H * 0.075, background: crimp, boxShadow: "inset 0 3px 5px rgba(0,0,0,0.5)" }} />

          <div style={{ position: "absolute", top: "17%", left: "50%", transform: "translateX(-50%)", filter: "drop-shadow(0 5px 11px rgba(0,0,0,0.55))" }}>
            <PokeBall d={W * 0.45} />
          </div>

          <div style={{ position: "absolute", bottom: H * 0.2, left: 0, right: 0, textAlign: "center" }}>
            <div style={{ fontSize: W * 0.2, fontWeight: 900, color: "#fff", letterSpacing: "-0.01em", textShadow: "0 2px 7px rgba(0,0,0,0.6)", lineHeight: 1 }}>{m.short}</div>
          </div>
          <div style={{ position: "absolute", bottom: H * 0.095, left: W * 0.08, right: W * 0.08, textAlign: "center" }}>
            <div style={{ fontSize: W * 0.068, fontWeight: 700, color: "rgba(255,255,255,0.9)", letterSpacing: "0.08em", lineHeight: 1.15 }}>
              {m.packSub || "1 GRADED CARD"}
            </div>
          </div>

          {/* Two sheens at different speeds — foil shifts, it doesn't just shine. */}
          <div
            style={{
              position: "absolute", top: "-20%", bottom: "-20%", left: 0, width: "45%",
              pointerEvents: "none", mixBlendMode: "screen", willChange: "transform",
              background: "linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.12) 35%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.12) 65%, transparent 100%)",
              transform: "translateX(-160%) skewX(-14deg)",
              animation: reduce ? "none" : "ceSheenX 5s cubic-bezier(.45,0,.55,1) infinite",
            }}
          />
          <div
            style={{
              position: "absolute", top: "-20%", bottom: "-20%", left: 0, width: "22%",
              pointerEvents: "none", mixBlendMode: "screen", willChange: "transform",
              background: "linear-gradient(105deg, transparent, rgba(190,225,255,0.45), transparent)",
              transform: "translateX(-160%) skewX(-14deg)",
              animation: reduce ? "none" : "ceSheenX 7.5s cubic-bezier(.45,0,.55,1) 1.2s infinite",
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function MarketObjectFace({ m, w = 160 }) {
  switch (m.obj) {
    case "slab":
      return <Slab3D m={m} size={w} />;
    case "pack":
    default:
      return <Pack3D m={m} size={w} />;
  }
}

/**
 * Wraps an object with perspective, an idle float, a price-direction glow and
 * an optional pointer tilt.
 * @param {{m: object, w?: number, interactive?: boolean, floaty?: boolean, onClick?: Function}} props
 */
export function MarketObject3D({ m, w = 160, interactive = false, floaty = true, onClick }) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const up = (m.change ?? 0) >= 0;
  const glow = up ? "rgba(22,181,127,0.45)" : "rgba(230,70,70,0.45)";

  const onMove = (e) => {
    if (!interactive) return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: -py * 16, y: px * 20 });
  };

  return (
    <div
      onMouseMove={onMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      onClick={onClick}
      style={{ perspective: 1000, cursor: onClick ? "pointer" : "default", filter: `drop-shadow(0 0 26px ${glow})`, transition: "filter .4s" }}
    >
      <div
        style={{
          transformStyle: "preserve-3d",
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: interactive ? "transform .12s ease-out" : "none",
          animation: floaty && !reduceMotion() ? "ceFloat 5s ease-in-out infinite" : "none",
        }}
      >
        <MarketObjectFace m={m} w={w} />
      </div>
    </div>
  );
}
